import { redirect } from '@tanstack/react-router';

import { navAccessByTarget, type NavTarget } from '@/components/layout/nav';
import { hasPermission, meetsRequirement } from '@/lib/accessControl';
import { type Role, useAuthStore } from '@/stores/auth';

/**
 * Route-level гард роли (Фаза 2 редизайна): пускает только перечисленные роли,
 * остальных уводит на /forbidden (403). Вызывается в ``beforeLoad`` ПОСЛЕ
 * родительского ``_authed`` (тот уже восстановил сессию), поэтому user здесь
 * всегда есть; ``null`` страхуем тихим пропуском — редирект на /login сделает
 * родитель.
 */
export function requireRole(...roles: Role[]): void {
  const user = useAuthStore.getState().user;
  if (user && !roles.includes(user.role)) {
    throw redirect({ to: '/forbidden' });
  }
}

export function requireCalculatorAccess(): void {
  const user = useAuthStore.getState().user;
  if (!user || user.role === 'owner') return;
  const allowed =
    user.role === 'admin'
      ? hasPermission(user.permissions, 'desmos', 'manage')
      : ['student', 'teacher'].includes(user.role) &&
        hasPermission(user.permissions, 'desmos', 'use');
  if (!allowed) throw redirect({ to: '/forbidden' });
}

/** Keep bookmarked administrator URLs aligned with the permission-aware sidebar. */
export function requireRouteAccess(target: NavTarget): void {
  const user = useAuthStore.getState().user;
  const requirement = navAccessByTarget.get(target);
  if (user?.role === 'admin' && requirement && !meetsRequirement(user.permissions, requirement)) {
    throw redirect({ to: '/forbidden' });
  }
}
